import React, { useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { obfuscatedCoordinates } from '../../util/maps';
import { Heading, Map, Modal } from '../../components';

import css from './ListingPage.module.css';

const EXPAND_MAP_FOCUS_ID = 'ListingPage.mapOpenTrigger';

/** Four-corner expand icon (visual affordance on the static map). */
const ExpandMapIcon = () => (
  <svg
    className={css.mapExpandIcon}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2 6V2h4M12 2h4v4M16 12v4h-4M6 16H2v-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The SectionMapMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.latlng} [props.geolocation] - The geolocation
 * @param {propTypes.uuid} props.listingId - The listing id
 * @param {Function} props.onManageDisableScrolling - Manage body scroll while modal is open
 * @returns {JSX.Element} section map maybe component
 */
const SectionMapMaybe = props => {
  const {
    className,
    rootClassName,
    geolocation,
    publicData,
    listingId,
    mapsConfig,
    onManageDisableScrolling,
  } = props;
  const intl = useIntl();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  if (!geolocation) {
    return null;
  }

  const address = publicData && publicData.location ? publicData.location.address : '';
  const pickupOnly = !!publicData?.pickupEnabled && !publicData?.shippingEnabled;
  const classes = classNames(rootClassName || css.sectionMap, className);
  const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

  const mapProps = mapsConfig.fuzzy.enabled
    ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey) }
    : { address, center: geolocation };

  const expandMapLabel = intl.formatMessage({ id: 'ListingPage.expandMap' });
  const closeMapLabel = intl.formatMessage({ id: 'ListingPage.closeMapModal' });
  const locationTitleId = pickupOnly
    ? 'ListingPage.pickupLocationTitle'
    : 'ListingPage.locationTitle';

  return (
    <section className={classes} id="listing-location">
      <div className={css.sectionHeadingRow}>
        <svg
          className={css.sectionHeaderIcon}
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" fill="none" />
          <circle cx="12" cy="10" r="2.6" fill="none" />
        </svg>
        <Heading as="h2" rootClassName={css.locationHeading}>
          <FormattedMessage id={locationTitleId} />
        </Heading>
      </div>
      <button
        id={EXPAND_MAP_FOCUS_ID}
        type="button"
        className={css.map}
        onClick={() => setIsMapModalOpen(true)}
        aria-label={expandMapLabel}
      >
        <Map {...mapProps} useStaticMap />
        <span className={css.mapExpandButton} aria-hidden="true">
          <ExpandMapIcon />
        </span>
      </button>
      {address ? (
        <p className={css.locationCaption}>
          <FormattedMessage id="ListingPage.approximateLocation" values={{ location: address }} />
        </p>
      ) : null}
      <Modal
        id="ListingPage.mapLightbox"
        scrollLayerClassName={css.mapModalScrollLayer}
        containerClassName={css.mapModalContainer}
        contentClassName={css.mapModalContent}
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={EXPAND_MAP_FOCUS_ID}
        closeButtonMessage={closeMapLabel}
      >
        {isMapModalOpen ? (
          <div className={css.mapModalMap}>
            <Map
              {...mapProps}
              useStaticMap={false}
              className={css.mapModalMapRoot}
              mapRootClassName={css.mapModalMapRoot}
            />
          </div>
        ) : null}
      </Modal>
    </section>
  );
};

export default SectionMapMaybe;
